"use client";

import React from "react";
import { Loader, Send, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function FreeTrialFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222234" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-none flex items-center justify-center mb-6">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">
          Subscription Confirmed
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" size="lg" className="rounded-none border-primary text-primary hover:bg-primary hover:text-white">
          Add Another Email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="rounded-none border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px]">Identify yourself for our records.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@advocates.org" className="rounded-none border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px]">Where we send our data-driven insights.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 202 555 0123" className="rounded-none border-border focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px]">Optional contact for urgent alerts.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="program" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Area of Interest</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="rounded-none border-border focus:ring-primary">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-none">
                  {["Governance & Policy", "Economic Transparency", "Civic Engagement", "Data Ethics"].map((program, i) => <SelectItem key={program} value={program} data-index={i} className="rounded-none">
                      {program}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="font-mono text-[10px]">Choose your primary research focus.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_start_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Membership Start Date (Optional)</FormLabel>
            <FormControl>
              <Input type="date" className="rounded-none border-border focus-visible:ring-primary" {...field} />
            </FormControl>
            <FormDescription className="font-mono text-[10px]">When would you like to begin receiving reports?</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold uppercase tracking-wider text-xs">Message to the Think Tank (Optional)</FormLabel>
            <FormControl>
              <textarea className="flex min-h-[100px] w-full rounded-none border border-input bg-transparent px-3 py-2 text-base shadow-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 resize-y" placeholder="How can we help you influence governance?" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-none flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-default">{submitError}</p>
        </div>}

      <Button type="submit" variant="default" size="lg" disabled={isSubmitting} className="w-full h-14 text-lg font-default font-bold rounded-none bg-primary text-white shadow-none hover:bg-primary/90 transition-all duration-300 uppercase tracking-widest">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            Be the Change
            <Send className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function BeginYourFreeTrial() {
  return <section id="join-the-network" className="relative py-24 lg:py-32 overflow-hidden bg-background text-foreground">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1644405/img/islamic_pattern_bg.png)] bg-cover bg-center opacity-[0.02] grayscale" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full bg-primary/5 blur-3xl rounded-full opacity-30" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-background border border-border rounded-none shadow-none p-6 md:p-10 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-primary" />

            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tighter uppercase">
                Join the Network
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto font-default">
                Empower your advocacy with data-driven tools. Subscribe to our research and influence responsive governance.
              </p>
            </div>

            <FormProvider formKey="join_form_home" formId="join_form_home" sectionName="Join the Network" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            program: "",
            preferred_start_date: "",
            additional_notes: ""
          }} validationRules={{
            full_name: {
              required: "Full name is required",
              minLength: {
                value: 2,
                message: "Name must be at least 2 characters"
              }
            },
            email: {
              required: "Email address is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            },
            phone: {
              required: "Phone number is required",
              minLength: {
                value: 8,
                message: "Please enter a valid phone number"
              }
            },
            program: {
              required: "Please select an area of interest"
            }
          }} submitText="Be the Change" successMessage="Your request has been logged. Our research team will reach out with your onboarding package shortly." errorMessage="Submission failed. Please verify your connection and try again.">
              <FreeTrialFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}